import { useEffect, useState } from "react";
import { Field } from "./Field";
import { Input } from "./Input";
import { isValidHex, normaliseHex } from "@/lib/utils/color";

/**
 * Hex colour input with a native swatch.
 *
 * Keeps its own text state so a half-typed value like "#d4f" doesn't get
 * normalised out from under the operator mid-keystroke; the parent only hears
 * about valid colours.
 */
export function ColorField({
  label, value, onChange, hint,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  hint?: string;
}) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const swatch = normaliseHex(draft) || "#000000";
  const invalid = draft.length > 0 && !isValidHex(draft);

  return (
    <Field label={label} hint={hint} error={invalid ? "Use a hex colour like #d4f14f" : undefined}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={swatch}
          onChange={(event) => { setDraft(event.target.value); onChange(event.target.value); }}
          className="h-10 w-12 shrink-0 rounded-md"
          aria-label={`${label} swatch`}
        />
        <Input
          value={draft}
          spellCheck={false}
          placeholder="#d4f14f"
          onChange={(event) => {
            setDraft(event.target.value);
            if (isValidHex(event.target.value)) onChange(normaliseHex(event.target.value));
          }}
          className="font-mono"
        />
      </div>
    </Field>
  );
}
